import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { INonConformanceTypeModel } from '../../models/types/non-conformance-type.model';
import { NonConformanceTypesApiService } from '../../services/types/non-conformance-types-api.service';
import {
  CreateNonConformanceType,
  DeleteNonConformanceType,
  GetNonConformanceTypeById,
  LoadAllNonConformanceTypes,
  LoadNonConformanceTypes,
  UpdateNonConformanceType,
} from './non-conformance-type.actions';

export interface NonConformanceTypeStateModel {
  nonConformanceTypes: INonConformanceTypeModel[] | undefined;
  allNonConformanceTypes: IdName[];
  nonConformanceTypesTotalPages: number;
}

const NON_CONFORMANCE_STATE_TOKEN = new StateToken<NonConformanceTypeStateModel>(
  'nonConformanceTypes'
);

@State<NonConformanceTypeStateModel>({
  name: NON_CONFORMANCE_STATE_TOKEN,
  defaults: {
    nonConformanceTypes: undefined,
    allNonConformanceTypes: [],
    nonConformanceTypesTotalPages: 0,
  },
})
@Injectable()
export class NonConformanceTypesState {
  constructor(private service: NonConformanceTypesApiService) {}

  @Action(LoadNonConformanceTypes, { cancelUncompleted: true })
  loadNonConformanceTypes(
    { getState, setState }: StateContext<NonConformanceTypeStateModel>,
    { filter }: LoadNonConformanceTypes
  ) {
    return this.service.getNonConformanceTypes(filter).pipe(
      tap((response: ApiResponse<INonConformanceTypeModel[]>) => {
        const state = getState();
        setState({
          ...state,
          nonConformanceTypes: response.data,
          nonConformanceTypesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllNonConformanceTypes, { cancelUncompleted: true })
  loadAllNonConformanceTypes(ctx: StateContext<NonConformanceTypeStateModel>) {
    return this.service.getAllNonConformanceTypes().pipe(
      tap((allNonConformanceTypes: IdName[]) => {
        ctx.setState(patch({ allNonConformanceTypes }));
      })
    );
  }

  @Action(GetNonConformanceTypeById, { cancelUncompleted: true })
  getNonConformanceTypeById(
    ctx: StateContext<NonConformanceTypeStateModel>,
    { id }: GetNonConformanceTypeById
  ) {
    // getting every existing non-conformance type from state
    const nonConformanceTypeState = ctx.getState().nonConformanceTypes;
    // if a competence group exist then just get updated (latest) from api
    if (nonConformanceTypeState?.find((ncType: INonConformanceTypeModel) => ncType.id === id)) {
      return this.service.getNonConformanceTypeById(id).pipe(
        tap((response: INonConformanceTypeModel) => {
          ctx.setState(
            patch({
              nonConformanceTypes: updateItem<INonConformanceTypeModel>(
                (toGet: INonConformanceTypeModel | undefined) => toGet?.id === response.id,
                response
              ),
            })
          );
        })
      );
    }
    // if it does not exist then fetch it from the api
    return this.service.getNonConformanceTypeById(id).pipe(
      tap((response: INonConformanceTypeModel) => {
        ctx.setState(
          patch({
            nonConformanceTypes: insertItem<INonConformanceTypeModel>(response),
          })
        );
      })
    );
  }

  @Action(CreateNonConformanceType, { cancelUncompleted: true })
  @OnSuccess({ url: AdminNonConformanceRoutes.Types, message: 'nonConformanceTypeCreated' })
  addNonConformanceType(
    ctx: StateContext<NonConformanceTypeStateModel>,
    { nonConformanceType }: CreateNonConformanceType
  ) {
    return this.service.createNonConformanceType(nonConformanceType).pipe(
      tap((response: INonConformanceTypeModel) => {
        ctx.setState(
          patch({
            nonConformanceTypes: insertItem<INonConformanceTypeModel>(response),
          })
        );
      })
    );
  }

  @Action(UpdateNonConformanceType, { cancelUncompleted: true })
  @OnSuccess({ url: AdminNonConformanceRoutes.Types, message: 'nonConformanceTypeUpdated' })
  updateNonConformanceType(
    ctx: StateContext<NonConformanceTypeStateModel>,
    { nonConformanceType }: UpdateNonConformanceType
  ) {
    return this.service.updateNonConformanceType(nonConformanceType).pipe(
      tap((response: INonConformanceTypeModel) => {
        ctx.setState(
          patch({
            nonConformanceTypes: updateItem<INonConformanceTypeModel>(
              (ncToUpdate: INonConformanceTypeModel | undefined) => ncToUpdate?.id === response.id,
              response
            ),
          })
        );
      })
    );
  }

  @Action(DeleteNonConformanceType, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminNonConformanceRoutes.Types,
    message: 'nonConformanceTypeDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteNonConformanceType(
    ctx: StateContext<NonConformanceTypeStateModel>,
    { id }: DeleteNonConformanceType
  ) {
    return this.service.deleteNonConformanceType(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            nonConformanceTypes: removeItem<INonConformanceTypeModel>(
              (ncToDelete: INonConformanceTypeModel | undefined) => ncToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Selector()
  static nonConformanceTypesTotalPages(state: NonConformanceTypeStateModel) {
    return state.nonConformanceTypesTotalPages;
  }

  @Selector()
  static getNonConformanceTypes(state: NonConformanceTypeStateModel) {
    return state.nonConformanceTypes;
  }

  @Selector()
  static getAllNonConformanceTypes(state: NonConformanceTypeStateModel) {
    return state.allNonConformanceTypes;
  }

  @Selector()
  static getNonConformanceTypeById(state: any) {
    return (id: string) => {
      return id
        ? state.nonConformanceTypes.find((ncType: INonConformanceTypeModel) => ncType.id === id)
        : null;
    };
  }
}
